import { useEffect, useState } from "react";
import { Row, Col, Button } from "reactstrap";
import { formatDate, dateIsSameOrAfter } from "../../Utilities/dateUtils";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import CheckInMap from "./CheckInMap";
import { FormattedMessage } from "react-intl";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  SetAttendanceValidity,
  PostAbsence,
} from "../../actions/teacherMutations";
import { toast } from "react-toastify";

export default function APLPresenceCheckTable({
  presenceData,
  absence,
  periodId,
  studentId,
  level,
  objId,
}) {
  const [checkIns, setCheckIns] = useState([]);
  const [checkOuts, setCheckOuts] = useState([]);
  const [mapIsOpen, setMapIsOpen] = useState(false);
  const [markersWithPosition, setMarkersWithPosition] = useState([]);
  const [markersData, setMarkersData] = useState([]);
  const setAttendanceValidity = useMutation({ mutationFn: SetAttendanceValidity });
  const postAbsence = useMutation({ mutationFn: PostAbsence });

  const queryClient = useQueryClient();

  useEffect(() => {
    let locationMarkers = [];
    presenceData.attendanceThisDay?.points.forEach((point) => {
      if (point.latitude && point.longitude) {
        const markerWithLocation = {
          position: { lat: point.latitude, lng: point.longitude },
          time: point.time,
          isCheckIn: point.isCheckIn,
          hasLocation: true,
        };
        locationMarkers.push(markerWithLocation);
      } else {
        const markerWithoutLocation = {
          position: { lat: null, lng: null },
          time: point.time,
          isCheckIn: point.isCheckIn,
          hasLocation: false,
        };
        locationMarkers.push(markerWithoutLocation);
      }
    });
    setMarkersData(locationMarkers);
  }, [presenceData]);

  useEffect(() => {
    let tempCheckins = [];
    let tempCheckouts = [];
    let tempMarkersWithPos = [];
    markersData.forEach((pd) => {
      if (pd.isCheckIn) {
        tempCheckins.push(pd);
      } else {
        tempCheckouts.push(pd);
      }
      if (pd.position.lat && pd.position.lng) {
        tempMarkersWithPos.push(pd);
      }
    });

    setCheckOuts(tempCheckouts);
    setCheckIns(tempCheckins);
    setMarkersWithPosition(tempMarkersWithPos);
  }, [markersData]);

  const showMap = () => {
    setMapIsOpen(true);
  };

  const checkIfPositionExists = () => {
    var noPositionFound = true;
    presenceData.attendanceThisDay?.points.forEach((pr) => {
      if (pr.latitute !== null && pr.longitude !== null) {
        noPositionFound = false;
      }
    });
    return noPositionFound;
  };

  const isTodayOrFuture = dateIsSameOrAfter(presenceData.day, new Date());

  const handleSetValidity = (wasPresent) => {
    setAttendanceValidity.mutate(
      {
        attendanceDay: presenceData.day,
        wasPresent: wasPresent,
        studentId: studentId,
        periodId: periodId,
      },
      {
        onSuccess: () => {
          toast.success(<FormattedMessage id="PresenceUpdated" />);
          queryClient.invalidateQueries({ queryKey: [
            "FetchAttendanceStatuses",
            [level, periodId, objId],
          ] });
        },
        onError: (error) => {
          toast.warn(
            error?.response?.data || <FormattedMessage id="GenericApiError" />,
          );
        },
      },
    );
  };

  const handleAddAbsence = () => {
    postAbsence.mutate(
      {
        studentId: studentId,
        absenceDay: presenceData.day,
        timeFrom: "0700",
        timeTo: "1600",
        comment: "",
        isWholeDay: false,
      },
      {
        onSuccess: () => {
          toast.success(<FormattedMessage id="PresenceUpdated" />);
          queryClient.invalidateQueries({ queryKey: [
            "FetchAttendanceStatuses",
            [level, periodId, objId],
          ] });
        },
        onError: (error) => {
          toast.warn(
            error?.response?.data || <FormattedMessage id="GenericApiError" />,
          );
        },
      },
    );
  };

  return (
    <Col>
      <div
        className="d-flex"
        style={{
          marginBottom: "1rem",
        }}
      >
        <Button
          outline
          disabled={checkIfPositionExists()}
          color={checkIfPositionExists() ? "secondary" : "primary"}
          className="mx-1 mb-0"
          onClick={() => showMap()}
        >
          <FontAwesomeIcon
            icon={"map-marker-alt"}
            style={{ marginRight: ".5rem" }}
          />
          <FormattedMessage id="ShowOnMap" />
        </Button>
      </div>
      <div className="d-flex">
        <Button
          className="mx-1"
          size="sm"
          outline
          color={isTodayOrFuture ? "secondary" : "primary"}
          disabled={isTodayOrFuture}
          onClick={() => handleAddAbsence()}
        >
          <FormattedMessage id="MarkAbsent" />
        </Button>

        <Button
          className="mx-1"
          size="sm"
          outline
          color={isTodayOrFuture ? "secondary" : "primary"}
          disabled={isTodayOrFuture}
          onClick={() => handleSetValidity(true)}
        >
          <FormattedMessage id="MarkAsPresent" />
        </Button>

        <Button
          className="mx-1"
          size="sm"
          outline
          color={isTodayOrFuture ? "secondary" : "primary"}
          disabled={isTodayOrFuture}
          onClick={() => handleSetValidity(false)}
        >
          <FormattedMessage id="MarkAsNotCheckedIn" />
        </Button>
      </div>
      {isTodayOrFuture && (
        <div
          className="d-flex align-items-center mx-1 mt-2"
          style={{ fontSize: "0.85rem", color: "#d69b15" }}
        >
          <FontAwesomeIcon icon="circle-info" className="me-1" />
          <FormattedMessage id="TodaysAttendanceCannotBeEdited" />
        </div>
      )}
      <hr />
      <Row>
        <Col lg={6}>
          <p className="lead" style={{ marginBottom: ".2rem" }}>
            <FormattedMessage id="CheckIns" />:
          </p>
          {checkIns.map((ci, i) => {
            if (!ci.noCheckins) {
              return (
                <p key={i} style={{ marginBottom: "0" }}>
                  {formatDate(ci.time, "HH:mm")}
                </p>
              );
            } else {
              return null;
            }
          })}
        </Col>
        <Col lg={6}>
          <p className="lead" style={{ marginBottom: ".2rem" }}>
            <FormattedMessage id="CheckOuts" />:
          </p>
          {checkOuts.map((co, i) => {
            if (!co.noCheckins) {
              return (
                <p key={i + "" + co.time} style={{ marginBottom: "0" }}>
                  {formatDate(co.time, "HH:mm")}
                </p>
              );
            } else {
              return null;
            }
          })}
        </Col>
      </Row>
      {absence && absence.length ? (
        <>
          <hr />
          <Row>
            <Col>
              <p className="lead" style={{ marginBottom: ".2rem" }}>
                <FormattedMessage id="Absence" />:
              </p>
            </Col>
          </Row>
          <Row>
            <Col>
              <FormattedMessage id="TimeFrom" />:
            </Col>
            <Col>
              <FormattedMessage id="TimeTo" />:
            </Col>
            <Col>
              <FormattedMessage id="Comment" />:
            </Col>
            <Col>
              <FormattedMessage id="CreatedByTeacher" />:
            </Col>
          </Row>
          {presenceData.absencesThisDay.map((ab, i) => {
            return (
              <Row key={studentId + "ab" + i}>
                <Col>{ab.timeFrom}</Col>
                <Col>{ab.timeTo}</Col>
                <Col>{ab.comment}</Col>
                <Col>
                  {ab.isCreatedByTeacher ? (
                    <FormattedMessage id="Yes" />
                  ) : (
                    <FormattedMessage id="No" />
                  )}
                </Col>
              </Row>
            );
          })}
        </>
      ) : (
        ""
      )}
      {markersWithPosition.length ? (
        <CheckInMap
          isOpen={mapIsOpen}
          toggle={() => setMapIsOpen(!mapIsOpen)}
          markerPoints={markersWithPosition}
          checkIns={checkIns}
          checkOuts={checkOuts}
        />
      ) : (
        ""
      )}
    </Col>
  );
}
